/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Session Expired Modal - Modal for handling session token issues
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import React from 'react'
import { AlertTriangle, LogIn } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { useAuthStore } from '../stores/authStore'
import ModalPortal from './ModalPortal'

const SessionExpiredModal = ({ isOpen, onClose, message, isSingleSessionLogout, code }) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { clearAuthData } = useAuthStore()

  const handleLoginRedirect = () => {
    // Clear all auth data and React Query cache
    clearAuthData(queryClient)

    // Close modal
    onClose()

    // Navigate to login
    navigate('/login', { replace: true })
  }

  if (!isOpen) return null

  // Determine title and default message based on error type
  const title =
    isSingleSessionLogout || code === 'SESSION_INVALIDATED'
      ? 'Session Invalidated'
      : 'Session Expired'

  const defaultMessage =
    isSingleSessionLogout || code === 'SESSION_INVALIDATED'
      ? 'You have been automatically logged out because you logged in from another device or browser. Only one active session is allowed per user for security reasons.'
      : 'There has been an issue with your authentication token. For security reasons, you must log in again to continue.'

  return (
    <ModalPortal isOpen={isOpen}>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black bg-opacity-50" />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
          {/* Icon */}
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/20 rounded-full">
            <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>

          {/* Content */}
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">{title}</h2>

            <p className="text-gray-600 dark:text-gray-400 mb-6">{message || defaultMessage}</p>

            {/* Action Button */}
            <button
              onClick={handleLoginRedirect}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              <LogIn className="w-5 h-5" />
              <span>Log In Again</span>
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  )
}

export default SessionExpiredModal
